/**
 * testimonials.ts — source of truth for real user testimonials.
 *
 * The landing page previously rendered two hardcoded fictional quotes
 * ("Fatima A. — saved AED 3,200 on a Chanel…") which looked like
 * social proof but wasn't. That's dishonest and it degrades trust when
 * a user notices. This file ships empty at launch and gets populated
 * as beta users submit real quotes.
 *
 * RULES:
 *   1. Only add a quote here when the user has given WRITTEN consent
 *      to be cited on the landing page. Informal "I like it!" Slack
 *      messages are not enough.
 *   2. `verified: true` only when (a) the user's name + city are
 *      accurate and (b) the quote is exactly as they wrote it.
 *   3. If a user wants to be anonymous, use first name only + city.
 *      Don't make up full names.
 *   4. All quotes must be bilingual (en + ar). If the user only gave
 *      one language, translate respectfully and set `translated: true`.
 *
 * Until at least 3 verified quotes are here, SocialProof renders a
 * "Be one of our first beta testers" call-to-action instead of quotes.
 */

export interface Testimonial {
  /** Short quote — under 180 chars ideally so the card reads cleanly. */
  text: { en: string; ar: string };
  /** First name + last initial, or first-name-only if anonymous preferred. */
  name: string;
  /** Two-letter initials for the avatar placeholder. */
  initials: string;
  /** City, country OR product category — whatever the user prefers. */
  role: { en: string; ar: string };
  /** ISO date the consent was recorded. */
  consentAt: string;
  /** true only when verified per RULES above. */
  verified: boolean;
  /** true when one language was translated from the other. */
  translated?: boolean;
}

/**
 * Populate as real quotes come in. Example shape shown in a comment
 * below so the next person knows what to slot in.
 */
export const TESTIMONIALS: Testimonial[] = [
  // Example (DO NOT UNCOMMENT as-is — replace with a real user):
  // {
  //   text: {
  //     en: 'Found a Gucci Marmont in my cart listed at AED 5,800 cheaper on Vestiaire — ordered from there instead.',
  //     ar: 'وجدت حقيبة غوتشي بسعر أقل بـ 5,800 درهم على Vestiaire — طلبت من هناك.',
  //   },
  //   name: 'Fatima A.',
  //   initials: 'FA',
  //   role: { en: 'Dubai, UAE', ar: 'دبي، الإمارات' },
  //   consentAt: '2026-04-20',
  //   verified: true,
  // },
];

export function verifiedTestimonials(): Testimonial[] {
  return TESTIMONIALS.filter((t) => t.verified);
}
